import { useQueryClient, type QueryClient } from '@tanstack/react-query';

import { currentTenantHost } from '@/config';
import { currentImpersonation } from '@/lib/platform/impersonation';
import { customerSession, staffSession } from '@/lib/session';

export interface QueryScope {
  tenantHost: string;
  actorId: string;
}

export function readQueryScope(): QueryScope {
  const impersonation = currentImpersonation();
  if (impersonation) {
    return {
      tenantHost: `${impersonation.slug}`,
      actorId: `impersonation:${impersonation.user.id}`,
    };
  }

  const staff = staffSession.get();
  if (staff) {
    return {
      tenantHost: currentTenantHost(),
      actorId: `${staff.user.scope}:${staff.user.id}`,
    };
  }

  const customer = customerSession.get();
  if (customer) {
    return {
      tenantHost: currentTenantHost(),
      actorId: `customer:${customer.customer.id}`,
    };
  }

  return { tenantHost: currentTenantHost(), actorId: 'anonymous' };
}

export function tenantKey(scope: QueryScope, parts: readonly unknown[]): readonly unknown[] {
  return ['tenant', scope.tenantHost, scope.actorId, ...parts];
}

export function invalidateTenantResource(client: QueryClient, resource: string): Promise<void> {
  const scope = readQueryScope();
  return client.invalidateQueries({ queryKey: tenantKey(scope, [resource]) });
}

export function useInvalidateTenantResource(): (resource: string) => Promise<void> {
  const client = useQueryClient();
  return (resource: string) => invalidateTenantResource(client, resource);
}
